<script setup lang="ts">
// Shared spinner shown inside a button while its click handlers are pending.
// Colours are passed in by the host button (CTAButton / Button), which resolve
// them from configurable fields that default to pipe CSS variables.
//
// The ring is fill-based (not stroke), so its width scales proportionally with
// `size` — `trackColor` paints the full background ring, `color` paints the
// rotating 3/4 arc on top.
import { computed } from 'vue'

type Props = {
  color: string
  trackColor?: string
  size?: string
}

const props = withDefaults(defineProps<Props>(), {
  trackColor: 'transparent',
  size: '1.5em',
})

const arcColor = computed(() => props.color)
const trackFill = computed(() => props.trackColor)
const loaderSize = computed(() => props.size)
</script>

<template>
  <span class="button-loader" role="status" aria-label="Loading">
    <svg
      class="button-loader__svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill="none"
    >
      <path
        :fill="trackFill"
        fill-rule="evenodd"
        d="M16 4.324C9.552 4.324 4.324 9.552 4.324 16S9.552 27.676 16 27.676 27.676 22.448 27.676 16c0-3.11-1.214-5.932-3.197-8.027A11.64 11.64 0 0 0 16 4.324M0 16C0 7.163 7.163 0 16 0a15.96 15.96 0 0 1 11.619 5A15.95 15.95 0 0 1 32 16c0 8.837-7.163 16-16 16S0 24.837 0 16"
        clip-rule="evenodd"
      />
      <path
        :fill="arcColor"
        fill-rule="evenodd"
        d="M16 4.324C9.552 4.324 4.324 9.552 4.324 16S9.552 27.676 16 27.676 27.676 22.448 27.676 16H32c0 8.837-7.163 16-16 16S0 24.837 0 16 7.163 0 16 0z"
        clip-rule="evenodd"
      />
    </svg>
  </span>
</template>

<style scoped lang="scss">
.button-loader {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &__svg {
    width: v-bind(loaderSize);
    height: v-bind(loaderSize);
    display: block;
    transform-origin: 50% 50%;
    animation: button-loader-rotate 0.9s linear infinite;
  }
}

@keyframes button-loader-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
